// src/components/App.tsx
import React, { useState, useEffect, Suspense, createContext, useRef } from "react";
import { lazy } from 'react';
import { useMsal } from "@azure/msal-react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  CircularProgress,
  Card,
  CardContent,
  Box,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import "./App.css";
import LoginIcon from "@mui/icons-material/Login";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MainLayout from "./MainLayout";
import Footer from "./Footer";
import AccessDenied from "./AccessDenied";
import TenantManager from "./TenantManager"; 
import { 
  BrowserRouter as Router, 
  Route, 
  Routes,
  useNavigate,
  useLocation,
  Navigate,
  createRoutesFromElements,
  Link
} from 'react-router-dom';
import "@fontsource/montserrat/700.css";
import LandingPage from "./LandingPage";
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';

// Import TokenService for consistent token management
import { TokenService } from '../utils/tokenService';
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import PopupBlockerDetector from "./PopupBlockerDetector";
import { clientId } from "../authConfig";

// NEW: Import the AppCacheProvider
import { AppCacheProvider } from '../context/AppCacheContext';

// Lazy load the MarketplaceLanding component
const MarketplaceLanding = lazy(() => import('./MarketplaceLanding'));

// ENHANCED: Create subscription context with app count limits
export const SubscriptionContext = createContext<{
  isSubscriptionActive: boolean;
  subscriptionData: any | null;
  subscriptionPlan: string;
  isLoading: boolean;
  // NEW: Add app limit information
  appLimit: number;
  canCreateApps: boolean;
}>({
  isSubscriptionActive: false,
  subscriptionData: null,
  subscriptionPlan: '',
  isLoading: true,
  appLimit: 20, // Default starter plan limit
  canCreateApps: true
});

// NEW: App plan configuration
const APP_COUNT_PLANS: Record<string, { maxApps: number; displayName: string }> = {
  starter: { maxApps: 20, displayName: 'Starter' },
  professional: { maxApps: 100, displayName: 'Professional' },
  enterprise: { maxApps: -1, displayName: 'Enterprise' } // unlimited
};

// Create a wrapper component to provide routing context to MainLayout
// This eliminates the need for MainLayout to have its own Router
const MainLayoutWrapper: React.FC<{
  isMsalInitialized: boolean;
  userDisplayName: string;
}> = ({ isMsalInitialized, userDisplayName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pass navigate function and location to MainLayout via props
  return (
    <MainLayout 
      isMsalInitialized={isMsalInitialized} 
      userDisplayName={userDisplayName} 
      // Add these props to pass routing functionality
      navigate={navigate}
      location={location}
      // This prop tells MainLayout not to use its own Router
      routerProvided={true}
    />
  );
};

// NEW: Subscription Provider with App Cache Integration
const SubscriptionProviderWithCache: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscriptionData, setSubscriptionData] = useState<any | null>(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean>(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('');
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState<boolean>(true);

  // Calculate app limits based on current plan
  const currentPlanConfig = APP_COUNT_PLANS[subscriptionPlan] || APP_COUNT_PLANS.starter;
  const appLimit = currentPlanConfig.maxApps;
  const canCreateApps = appLimit === -1; // Enterprise has unlimited

  // Load subscription on mount
  useEffect(() => {
    const checkExistingSubscription = () => {
      try {
        const storedSubscription = localStorage.getItem('marketplaceSubscription');
        if (storedSubscription) {
          const parsedSubscription = JSON.parse(storedSubscription);
          setSubscriptionData(parsedSubscription);
          setSubscriptionPlan(parsedSubscription.planId || 'starter');
          setIsSubscriptionActive(true);
          console.log('📋 Loaded subscription:', parsedSubscription.planId, `(${currentPlanConfig.displayName})`);
        } else {
          // Default to starter plan if no subscription found
          setSubscriptionPlan('starter');
          console.log('📋 No subscription found, defaulting to starter plan');
        }
      } catch (error) {
        console.error("Error loading subscription data:", error);
        setSubscriptionPlan('starter'); // Fallback to starter
      } finally {
        setIsSubscriptionLoading(false);
      }
    };
    
    checkExistingSubscription();
  }, []);

  // Handler for successful subscription activation
  const handleSubscriptionComplete = (data: any) => {
    console.log("Subscription activated successfully:", data);
    setSubscriptionData(data);
    setSubscriptionPlan(data.planId || 'starter');
    setIsSubscriptionActive(true);
    setIsSubscriptionLoading(false);
    
    // Store in localStorage for persistence across sessions
    localStorage.setItem('marketplaceSubscription', JSON.stringify({
      subscriptionId: data.id,
      planId: data.planId,
      activatedOn: new Date().toISOString(),
      status: data.status || 'Active'
    }));
  };

  // Handler for subscription errors
  const handleSubscriptionError = (error: Error) => {
    console.error("Subscription activation error:", error);
    setIsSubscriptionLoading(false);
  };

  return (
    <SubscriptionContext.Provider value={{
      isSubscriptionActive,
      subscriptionData,
      subscriptionPlan,
      isLoading: isSubscriptionLoading,
      appLimit,
      canCreateApps
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

const App: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);
  const [tenantCheckComplete, setTenantCheckComplete] = useState(false);
  const [isTenantDisallowed, setIsTenantDisallowed] = useState(false);
  const [isTenantOnboarded, setIsTenantOnboarded] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const userDisplayName = accounts.length > 0 ? accounts[0].name || accounts[0].username : null;
  const tenantId = accounts.length > 0 ? accounts[0].tenantId : null;

  // === UPDATED (Inactivity Auto Sign-Out): BEGIN ===
  // Automatically sign the user out after a period of inactivity (default: 15 minutes).
  // We attach lightweight global listeners that reset a timer on user activity.
  // When the timer elapses, we log out via redirect to avoid popup blockers.
  const INACTIVITY_TIMEOUT_MINUTES = 30; // adjust as needed
  const INACTIVITY_TIMEOUT_MS = INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
  // --- NEW: Pre-warning configuration (e.g., show dialog 120 seconds before sign-out)
  const WARNING_SECONDS = 180; // 3 minutes
  const WARNING_MS = WARNING_SECONDS * 1000;
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [warningSecondsLeft, setWarningSecondsLeft] = useState<number>(WARNING_SECONDS);

  // --- FIXED: Use useRef to track timers across renders without causing re-renders
  const timersRef = useRef<{
    warningTimer?: number;
    logoutTimer?: number;
    countdownInterval?: number;
  }>({});

  useEffect(() => {
    // Only enable when a user is signed in and we're not inside a popup window
    if (!userDisplayName || typeof window === 'undefined' || (window as any).opener) return;

    // --- FIXED: Add console logging for debugging
    console.log('[AutoSignOut] Effect mounted. Timeout:', INACTIVITY_TIMEOUT_MINUTES, 'minutes');

    const clearAllTimers = () => {
      if (timersRef.current.warningTimer) {
        window.clearTimeout(timersRef.current.warningTimer);
        timersRef.current.warningTimer = undefined;
      }
      if (timersRef.current.logoutTimer) {
        window.clearTimeout(timersRef.current.logoutTimer);
        timersRef.current.logoutTimer = undefined;
      }
      if (timersRef.current.countdownInterval) {
        window.clearInterval(timersRef.current.countdownInterval);
        timersRef.current.countdownInterval = undefined;
      }
    };

    const startCountdown = () => {
      setWarningSecondsLeft(WARNING_SECONDS);
      if (timersRef.current.countdownInterval) {
        window.clearInterval(timersRef.current.countdownInterval);
      }
      timersRef.current.countdownInterval = window.setInterval(() => {
        setWarningSecondsLeft((prev) => {
          if (prev <= 1) {
            // Let the logout timer handle the redirect, just stop the interval here
            if (timersRef.current.countdownInterval) {
              window.clearInterval(timersRef.current.countdownInterval);
              timersRef.current.countdownInterval = undefined;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const scheduleTimers = () => {
      clearAllTimers();
      setShowInactivityWarning(false);
      setWarningSecondsLeft(WARNING_SECONDS);

      // --- FIXED: Log when timers are scheduled
      // const now = new Date().toLocaleTimeString();
      // console.log('[AutoSignOut] Timers scheduled at', now);

      // Show warning dialog WARNING_SECONDS before final logout
      const warningDelay = Math.max(0, INACTIVITY_TIMEOUT_MS - WARNING_MS);
      timersRef.current.warningTimer = window.setTimeout(() => {
        console.log('[AutoSignOut] Warning dialog triggered');
        setShowInactivityWarning(true);
        startCountdown();
      }, warningDelay);

      // Final logout at inactivity threshold
      timersRef.current.logoutTimer = window.setTimeout(() => {
        try {
          console.info("[AutoSignOut] Inactivity threshold reached. Signing out...");
          // === UPDATED: Dismiss warning dialog immediately before logout
          // Hide the warning dialog to avoid confusing UX where user sees "Stay signed in" 
          // button after they've already been signed out
          setShowInactivityWarning(false);
          
          // === UPDATED: User-friendly auto sign-out with immediate re-login prompt
          // Instead of showing account selection popup, clear the session and redirect to sign-in.
          // This provides a better UX: user returns to the app and sees sign-in screen instead of logout popup.
          const currentAccount = accounts.length > 0 ? accounts[0] : undefined;
          instance.logoutRedirect({ 
            postLogoutRedirectUri: window.location.origin,
            account: currentAccount, // Hint which account to sign out
            onRedirectNavigate: () => {
              // After logout redirect completes, the app will reload and show the sign-in card
              // No account selection popup will appear since session is cleared
              return false; // Allow default navigation
            }
          });
        } catch (e) {
          console.error("[AutoSignOut] Logout redirect failed:", e);
        }
      }, INACTIVITY_TIMEOUT_MS);
    };

    // --- FIXED: Use callback ref pattern to avoid stale closure issues
    const handleActivity = () => {
      // Avoid extending sessions while tab is hidden; resume on visible
      if (document.visibilityState === 'visible') {
        // --- FIXED: Use functional state update to check current warning state
        setShowInactivityWarning((currentWarning) => {
          if (currentWarning) {
            console.log('[AutoSignOut] User activity detected while warning visible - resetting timers');
          }
          return false;
        });
        scheduleTimers();
      }
    };

    const windowEvents: (keyof WindowEventMap)[] = [
      'mousemove',
      'keydown',
      'mousedown',
      'touchstart',
      'scroll'
    ];

    windowEvents.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true } as EventListenerOptions));
    // Listen to document visibility changes separately (document-level event)
    document.addEventListener('visibilitychange', handleActivity);
    // Start timers immediately on mount
    scheduleTimers();

    return () => {
      console.log('[AutoSignOut] Effect cleanup - clearing all timers');
      clearAllTimers();
      windowEvents.forEach((evt) => window.removeEventListener(evt, handleActivity));
      document.removeEventListener('visibilitychange', handleActivity);
    };
  }, [userDisplayName, instance, INACTIVITY_TIMEOUT_MS, WARNING_MS]); // --- FIXED: Removed showInactivityWarning from deps
  // === UPDATED (Inactivity Auto Sign-Out): END ===

  const [allowedTenantIds, setAllowedTenantIds] = useState<string[]>([]);
  const [tenantConfigLoaded, setTenantConfigLoaded] = useState(false);
  const [configSource, setConfigSource] = useState<'api' | 'fallback' | 'none'>('none');

  // Subscription state moved to SubscriptionProviderWithCache
  const [subscriptionData, setSubscriptionData] = useState<any | null>(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean>(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('');
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState<boolean>(true);

  // Load tenant configuration on app start
  useEffect(() => {
    const loadTenantConfig = async () => {
      try {
        // First: Try the dynamic API
        console.log('Attempting to load tenant config from API...');
        const apiResponse = await fetch('/api/get-allowed-tenants', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        
        if (apiResponse.ok) {
          const config = await apiResponse.json();
          setAllowedTenantIds(config.allowedTenantIds);
          setConfigSource('api');
          console.log('✅ Tenant config loaded from API:', config.allowedTenantIds.length, 'tenants');
          return;
        }
        
        // Enhanced error logging
        console.warn(`API failed with status: ${apiResponse.status} ${apiResponse.statusText}`);
        const errorText = await apiResponse.text();
        console.warn('API error response:', errorText.substring(0, 200));
        
        throw new Error(`API failed with status: ${apiResponse.status}`);
        
      } catch (apiError) {
        console.warn('❌ API failed, trying fallback file...', apiError);
        
        try {
          // Fallback: Load from static JSON file
          const fallbackResponse = await fetch('/config/allowed-tenants.json');
          
          if (fallbackResponse.ok) {
            const fallbackConfig = await fallbackResponse.json();
            setAllowedTenantIds(fallbackConfig.allowedTenantIds);
            setConfigSource('fallback');
            console.log('✅ Tenant config loaded from fallback file:', fallbackConfig.allowedTenantIds.length, 'tenants');
            return;
          }
          
          throw new Error(`Fallback file failed with status: ${fallbackResponse.status}`);
          
        } catch (fallbackError) {
          console.error('❌ Both API and fallback failed:', fallbackError);
          
          // Last resort: Empty array (blocks all tenants - fail safe)
          setAllowedTenantIds([]);
          setConfigSource('none');
          console.error('🚫 No tenant configuration available - blocking all access');
        }
      } finally {
        setTenantConfigLoaded(true);
      }
    };

    loadTenantConfig();
  }, []);

  const getTenantDisplayText = (tenantId: string): string => {
    return `Tenant ID: ${tenantId}`;
  };

  const handlePopupBlocked = (isBlocked: boolean) => {
    console.log("Popup blocking detected:", isBlocked);
  };

  // Initialize MSAL
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await instance.initialize();
        const response = await instance.handleRedirectPromise();
        if (response) {
          console.log("Redirect response:", response);
        }
      } catch (error) {
        console.error("Error handling redirect:", error);
      } finally {
        setIsMsalInitialized(true);
      }
    };
    initializeMsal();
  }, [instance]);

  // Check if the tenant is allowed and onboarded
  useEffect(() => {
    if (!tenantCheckComplete && accounts.length > 0 && tenantConfigLoaded) {
      const tenantId = accounts[0].tenantId;
      console.log("Signed-in tenant ID:", tenantId);
      console.log("Config source:", configSource);

      // If no config available, block access
      if (configSource === 'none' || allowedTenantIds.length === 0) {
        console.log("🚫 No tenant configuration - access blocked");
        setIsTenantDisallowed(true);
        setTenantCheckComplete(true);
        return;
      }

      // Check if tenant is in allowlist
      if (!allowedTenantIds.includes(tenantId)) {
        console.log("🚫 Tenant not in allowlist - access blocked");
        setIsTenantDisallowed(true);
        setTenantCheckComplete(true);
        return;
      }

      console.log("✅ Tenant approved - proceeding with onboarding check");
      
      let isMounted = true;
      
      verifyTenantOnboarding(tenantId)
        .then(isOnboarded => {
          if (!isMounted) return;
          
          console.log("Tenant onboarding verification result:", isOnboarded);
          
          if (isOnboarded) {
            setIsTenantOnboarded(true);
            setShowOnboarding(false);
          } else {
            setShowOnboarding(true);
            setIsTenantOnboarded(false);
          }
          
          setTenantCheckComplete(true);
        })
        .catch(error => {
          if (!isMounted) return;
          
          console.error("Error verifying tenant onboarding:", error);
          setShowOnboarding(true);
          setIsTenantOnboarded(false);
          setTenantCheckComplete(true);
        });
        
      return () => {
        isMounted = false;
      };
    }
  }, [accounts, tenantCheckComplete, allowedTenantIds, tenantConfigLoaded, configSource, instance]);

  async function verifyTenantOnboarding(tenantId: string): Promise<boolean> {
    // For critical tenants, always return true without API check
    const criticalTenants = [
      "ecca902a-0511-4b72-9fa9-e3b0cafc1e09", // Your main tenant
    ];
    
    if (criticalTenants.includes(tenantId)) {
      console.log("Critical tenant detected, skipping API verification:", tenantId);
      return true;
    }

    // Add a small delay to ensure MSAL token cache is ready after page refresh
    if (performance.now() < 5000) { // If page loaded less than 5 seconds ago
      console.log("Page recently loaded, waiting for MSAL token cache to stabilize...");
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    try {
      console.log("Verifying tenant onboarding via Microsoft Graph API");
      
      // Enhanced retry logic for page refresh scenarios
      const maxRetries = 2;
      let lastError: any;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${2000 * attempt}ms delay`);
            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
          }
          
          // Try to acquire a token with the high-privilege scope
          const tokenPromise = instance.acquireTokenSilent({
            scopes: ["https://graph.microsoft.com/Application.ReadWrite.All"],
            account: accounts[0],
            forceRefresh: attempt > 0 // Force refresh on retry
          });
          
          // Increased timeout for page refresh scenarios
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error("Token acquisition timed out")), 15000);
          });
          
          // Race the token acquisition against the timeout
          const tokenResponse = await Promise.race([tokenPromise, timeoutPromise]);
          
          // Make a test call to verify permissions
          const response = await fetch('https://graph.microsoft.com/v1.0/applications?$top=1', {
            headers: {
              'Authorization': `Bearer ${tokenResponse.accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            console.log("Successfully verified Application.ReadWrite.All permission");
            return true;
          } else if (response.status === 403) {
            // Forbidden - permissions definitely not granted
            console.log("API call forbidden - tenant needs onboarding");
            return false;
          } else {
            // Other HTTP errors - might be temporary, continue to next attempt
            const errorText = await response.text();
            console.warn(`API call failed with status ${response.status}: ${errorText}`);
            lastError = new Error(`HTTP ${response.status}: ${errorText}`);
            continue;
          }
        } catch (error) {
          lastError = error;
          
          if (error instanceof InteractionRequiredAuthError || 
              (error instanceof Error && error.message.includes("consent_required"))) {
            // Definitive consent required - don't retry
            console.log("Consent required, tenant needs onboarding:", error);
            return false;
          }
          
          if (error instanceof Error && 
              error.message.includes("uninitialized_public_client_application")) {
            // MSAL not ready - continue retrying
            console.warn("MSAL not ready, will retry:", error);
            continue;
          }
          
          // For other errors, continue retrying
          console.warn(`Verification attempt ${attempt + 1} failed:`, error);
        }
      }
      
      // All attempts failed
      console.error("All verification attempts failed:", lastError);
      return false;
      
    } catch (error) {
      console.error("Unexpected error during tenant verification:", error);
      return false;
    }
  }    

  function clearTenantOnboardingStatus(tenantId: string): void {
    console.log(`Clearing onboarding status for tenant: ${tenantId}`);
  }

  // Handler functions (moved from removed state management)
  const handleSubscriptionComplete = (data: any) => {
    console.log("Subscription activated successfully:", data);
    setSubscriptionData(data);
    setSubscriptionPlan(data.planId || 'starter');
    setIsSubscriptionActive(true);
    setIsSubscriptionLoading(false);
    
    // Store in localStorage for persistence across sessions
    localStorage.setItem('marketplaceSubscription', JSON.stringify({
      subscriptionId: data.id,
      planId: data.planId,
      activatedOn: new Date().toISOString()
    }));
  };

  const handleSubscriptionError = (error: Error) => {
    console.error("Subscription activation error:", error);
    setIsSubscriptionLoading(false);
  };

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    console.log("Onboarding complete for tenant:", tenantId);
    setIsTenantOnboarded(true);
    setShowOnboarding(false);
  };

  const handleCancelOnboarding = () => {
    // Reset the onboarding state
    setIsTenantDisallowed(false);
    setTenantCheckComplete(false);
    setShowOnboarding(false);
    
    // Sign out the current user
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin
    }).catch(error => {
      console.error("Error during logout:", error);
    });
  };

  // Sign-in / Sign-out
  const handleLogin = async () => {
    if (window.opener) {
      console.warn("Skipping loginPopup because we're already in a popup window.");
      return;
    }
  
    try {
      await instance.loginPopup({
        scopes: ["openid", "profile", "email"],
      });
    } catch (error: any) {
      console.error("loginPopup failed:", error);
      instance.loginRedirect({
        scopes: ["openid", "profile", "email"],
        prompt: "consent"
      });
    }
  };
  
  const handleLogout = () => {
    if (window.opener) {
      console.warn("Skipping logout because we're already in a popup window.");
      return;
    }
    const currentAccount = accounts.length > 0 ? accounts[0] : undefined;
    instance.logoutRedirect({
      account: currentAccount,
      postLogoutRedirectUri: window.location.origin,
    }).catch((error) => {
      console.error("Logout failed:", error);
    });
  };

  const handleTryAnotherAccount = async () => {
    try {
      await instance.logoutPopup();
    } catch (e) {
      console.error("Error logging out:", e);
    } finally {
      setIsTenantDisallowed(false);
      setTenantCheckComplete(false);
    }
  };

  // If still initializing, show spinner
  if (!isMsalInitialized) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Initializing...
        </Typography>
      </Container>
    );
  }

  // If tenant is disallowed, show AccessDenied
  if (isTenantDisallowed) {
    return <AccessDenied onTryDifferentAccount={handleTryAnotherAccount} tenantId={tenantId || undefined} />;
  }

  // UPDATED: Render with new provider hierarchy
  return (
    <PopupBlockerDetector 
      onDetectBlocked={handlePopupBlocked}
      checkOnMount={true}
      showWarning={true}
    >  
      {/* NEW: Provider hierarchy with AppCache at the top level */}
      <AppCacheProvider>
        <SubscriptionProviderWithCache>
          <Router>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                background: "linear-gradient(135deg, #f8fafb 0%, #e9ecef 50%, #f1f3f4 100%)", // More sophisticated gradient
                position: 'relative'
              }}
            >
              {/* === UPDATED (Inactivity Warning Dialog): BEGIN === */}
              <Dialog open={showInactivityWarning} onClose={() => { /* prevent closing without action */ }}>
                <DialogTitle>Session will expire soon</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">
                    You’ve been inactive for a while. For your security, you’ll be signed out in {warningSecondsLeft}s.
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      // Treat as user activity: hide dialog and reset timers via activity handler
                      setShowInactivityWarning(false);
                      // Trigger a synthetic activity reset by dispatching an event
                      window.dispatchEvent(new Event('mousemove'));
                    }}
                  >
                    Stay signed in
                  </Button>
                </DialogActions>
              </Dialog>
              {/* === UPDATED (Inactivity Warning Dialog): END === */}
              <AppBar position="static" sx={{ 
                backgroundColor: "#e8e8e8", // Slightly warmer gray
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)', // Subtle bottom border
              }} elevation={2}> {/* Reduced elevation for cleaner look */}
                <Toolbar sx={{ minHeight: '72px' }}> {/* Slightly taller for better proportion */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <img
                      src="/logo-nobackground-5000-orange-grad-wheel.png"
                      alt="AppConfig² Logo"
                      width="140" // Slightly smaller in header
                      style={{ 
                        marginRight: 10,
                        filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.1))' // Subtle shadow for better definition
                      }}
                    />
                  </Box>

                  {userDisplayName ? (
                    <Typography
                      variant="subtitle1"
                      component="div"
                      sx={{
                        flexGrow: 1,
                        fontWeight: 500,
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        textAlign: "center",
                        color: "#333333"
                      }}
                    >
                      {tenantId ? getTenantDisplayText(tenantId) : "Unknown Tenant"}
                    </Typography>
                  ) : (
                    <div style={{ flexGrow: 1 }} />
                  )}

                  {userDisplayName && (
                    <>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          mr: 2, 
                          color: "#333333",
                          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                          fontWeight: 400,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <span style={{ color: '#555555', marginRight: '4px' }}>
                          Signed in as:
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          {userDisplayName}
                        </span>
                      </Typography>
                      <Button
                        variant="outlined"
                        sx={{ 
                          color: "#333333", 
                          borderColor: "#777777",
                          borderRadius: 2,
                          py: 0.5,
                          textTransform: 'none',
                          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#ff6b00',
                            backgroundColor: 'rgba(255, 107, 0, 0.04)',
                            color: '#ff6b00'
                          }
                        }}
                        onClick={handleLogout}
                      >
                        Sign Out
                      </Button>
                    </>
                  )}
                </Toolbar>
              </AppBar>

              <Routes>
                {/* Public Landing Page with Onboarding Info */}
                <Route path="/getting-started" element={<LandingPage />} />

                {/* Marketplace Landing Route */}
                <Route path="/marketplace-landing" element={
                  <Suspense fallback={
                    <Container sx={{ mt: 4, textAlign: "center" }}>
                      <CircularProgress />
                      <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading marketplace subscription setup...
                      </Typography>
                    </Container>
                  }>
                    <MarketplaceLanding
                      onSubscriptionComplete={handleSubscriptionComplete}
                      onSubscriptionError={handleSubscriptionError}
                    />
                  </Suspense>
                } />          
                
                {/* Add SAML Tester route */}
                <Route path="/samlTester" element={
                  userDisplayName ? (
                    <MainLayoutWrapper 
                      isMsalInitialized={true} 
                      userDisplayName={userDisplayName} 
                    />
                  ) : (
                    <Navigate to="/" replace />
                  )
                } />
                
                {/* Main App Route */}
                <Route path="/*" element={
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {userDisplayName ? (
                      tenantCheckComplete && showOnboarding ? (
                        <TenantManager
                          clientId={clientId}
                          onOnboardingComplete={handleOnboardingComplete}
                          onCancel={handleCancelOnboarding}
                          tenantVerified={isTenantOnboarded} 
                        />
                      ) : (
                        <MainLayoutWrapper 
                          isMsalInitialized={true} 
                          userDisplayName={userDisplayName} 
                        />
                      )
                    ) : (
                      <Container sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
                        <Card sx={{ 
                          p: 4, 
                          maxWidth: 550, 
                          textAlign: "center", 
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)', // Softer, more modern shadow
                          borderRadius: 4, // Increased border radius
                          border: '1px solid rgba(255, 255, 255, 0.2)', // Subtle border
                          background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)', // Very subtle gradient
                        }}>
                          <CardContent sx={{ '&:last-child': { pb: 4 } }}>
                            <Box sx={{ mb: 4 }}>
                              <img
                                src="/logo-nobackground-5000-orange-grad-wheel.png"
                                alt="AppConfig² Logo"
                                width="320"
                                className="logo"
                                style={{ 
                                  marginBottom: 0,
                                  filter: 'drop-shadow(0 2px 8px rgba(255, 107, 0, 0.15))'
                                }}
                              />
                            </Box>

                            {/* Primary action: Sign-in button */}
                            <Button
                              variant="contained"
                              onClick={handleLogin}
                              size="large"
                              sx={{
                                width: "100%",
                                py: 1.8,
                                fontSize: "1.1rem",
                                fontFamily: "'Segoe UI', system-ui, sans-serif",
                                fontWeight: 600,
                                letterSpacing: "0.01em",
                                backgroundColor: "#ff6b00",
                                borderRadius: 3,
                                boxShadow: "0 4px 16px rgba(255, 107, 0, 0.3)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  backgroundColor: "#e65c00",
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 8px 24px rgba(255, 107, 0, 0.4)"
                                },
                                "&:active": {
                                  transform: "translateY(0px)",
                                  transition: "all 0.1s"
                                }
                              }}
                              startIcon={<LoginIcon />}
                            >
                              Sign In with Microsoft
                            </Button>
                            
                            {/* Secondary action: Setup guide link */}
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                              <Typography 
                                variant="body2" 
                                color="textSecondary"
                                sx={{ display: 'inline', mr: 1 }}
                              >
                                First-time user?
                              </Typography>
                              <MuiLink 
                                href="/getting-started" 
                                target="_blank"
                                sx={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center',
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: '#1976d2',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                View setup guide <OpenInNewIcon fontSize="small" sx={{ ml: 0.5, fontSize: '1rem' }} />
                              </MuiLink>
                            </Box>
                          </CardContent>
                        </Card>
                      </Container>
                    )}
                  </Box>
                } />
              </Routes>

              <Footer />
            </div>
          </Router>
        </SubscriptionProviderWithCache>
      </AppCacheProvider>
    </PopupBlockerDetector>
  );
};

export default App;